import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BriefcaseIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
  ClockIcon,
  AcademicCapIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface CourseRequirement {
  courseId: string;
  courseName: string;
  minimumGrade: number;
}

interface JobFormData {
  title: string;
  description: string;
  requirements: string[];
  skillsRequired: string[];
  location: string;
  remoteType: 'onsite' | 'remote' | 'hybrid';
  jobType: 'full-time' | 'part-time' | 'contract' | 'internship';
  experienceLevel: 'entry' | 'mid' | 'senior' | 'executive';
  salaryRange: {
    min: number;
    max: number;
    currency: string;
  };
  benefits: string[];
  companyDescription: string;
  
  // Auto-offer fields
  autoOfferEnabled: boolean;
  autoOfferTimeLimit: number; // in hours
  autoOfferRequiredCourses: CourseRequirement[];
  autoOfferMinimumScore?: number;
}

const PostJobPage: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    description: '',
    requirements: [''],
    skillsRequired: [''],
    location: '',
    remoteType: 'onsite',
    jobType: 'full-time',
    experienceLevel: 'mid',
    salaryRange: {
      min: 50000,
      max: 100000,
      currency: 'USD'
    },
    benefits: [''],
    companyDescription: '',
    
    // Auto-offer defaults
    autoOfferEnabled: false,
    autoOfferTimeLimit: 72, // 3 days default
    autoOfferRequiredCourses: [],
    autoOfferMinimumScore: 80
  });
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<Array<{id: string, title: string}>>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const handleInputChange = (field: keyof JobFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field: keyof JobFormData, index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).map((item, i) => i === index ? value : item)
    }));
  };

  const handleCourseRequirementChange = (index: number, field: keyof CourseRequirement, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      autoOfferRequiredCourses: prev.autoOfferRequiredCourses.map((course, i) => 
        i === index ? { ...course, [field]: value } : course
      )
    }));
  };

  const addCourseRequirement = () => {
    setFormData(prev => ({
      ...prev,
      autoOfferRequiredCourses: [
        ...prev.autoOfferRequiredCourses,
        { courseId: '', courseName: '', minimumGrade: 70 }
      ]
    }));
  };

  const removeCourseRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      autoOfferRequiredCourses: prev.autoOfferRequiredCourses.filter((_, i) => i !== index)
    }));
  };

  // Load available courses
  useEffect(() => {
    const loadCourses = async () => {
      if (!formData.autoOfferEnabled) return;
      
      setLoadingCourses(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/courses`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setAvailableCourses(result.data.map((course: any) => ({
              id: course.id,
              title: course.title
            })));
          }
        }
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    loadCourses();
  }, [formData.autoOfferEnabled]);

  const addArrayItem = (field: keyof JobFormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] as string[]), '']
    }));
  };

  const removeArrayItem = (field: keyof JobFormData, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index)
    }));
  };

  const handleAIGeneration = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a job title or description');
      return;
    }

    setAiGenerating(true);

    // Check if user has access to AI generation features and generate content
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          contentType: 'complete_job',
          currentJobData: formData
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.upgradeRequired) {
          setShowUpgradeModal(true);
          return;
        }
        throw new Error(errorData.message || 'Access check failed');
      }

      const result = await response.json();
      if (result.success && result.data.content) {
        // Parse the generated content and update form
        const content = result.data.content;
        setFormData(prev => ({
          ...prev,
          title: content.title || aiPrompt,
          description: content.description || prev.description,
          skillsRequired: content.skillsRequired || prev.skillsRequired,
          requirements: content.requirements || prev.requirements
        }));

        setShowAIModal(false);
        setAiPrompt('');
        toast.success('Job description generated successfully with your company insights!');
        setAiGenerating(false);
        return;
      }
    } catch (error) {
      console.error('Error generating AI content:', error);
      setShowUpgradeModal(true);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Clean up form data - remove empty strings from arrays
      const cleanedData = {
        ...formData,
        requirements: formData.requirements.filter(req => req.trim() !== ''),
        skillsRequired: formData.skillsRequired.filter(skill => skill.trim() !== ''),
        benefits: formData.benefits.filter(benefit => benefit.trim() !== ''),
        autoOfferRequiredCourses: formData.autoOfferRequiredCourses.filter(course => 
          course.courseId && course.courseName
        ),
        // Only include auto-offer data if enabled
        ...(formData.autoOfferEnabled && {
          autoOffer: {
            enabled: true,
            timeLimit: formData.autoOfferTimeLimit,
            requiredCourses: formData.autoOfferRequiredCourses.filter(course => 
              course.courseId && course.courseName
            ),
            minimumOverallScore: formData.autoOfferMinimumScore,
            createdAt: new Date()
          }
        })
      };
      
      // Make actual API call to backend
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(cleanedData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create job');
      }
      
      toast.success('Job posted successfully!');
      navigate('/employer/job-postings'); // Navigate to job postings instead of analytics
    } catch (error: any) {
      console.error('Error posting job:', error);
      toast.error(error.message || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BriefcaseIcon className="h-8 w-8 text-indigo-600" />
            Post a New Job
          </h1>
          <p className="mt-2 text-gray-600">
            Create a compelling job posting to attract the best candidates
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
              <button
                type="button"
                onClick={() => setShowAIModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                <SparklesIcon className="h-4 w-4" />
                AI Generate
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Senior React Developer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <div className="relative">
                  <MapPinIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., San Francisco, CA"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Type
                </label>
                <select
                  value={formData.remoteType}
                  onChange={(e) => handleInputChange('remoteType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="onsite">On-site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Type
                </label>
                <select
                  value={formData.jobType}
                  onChange={(e) => handleInputChange('jobType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level
                </label>
                <select
                  value={formData.experienceLevel}
                  onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Description</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={8}
                  placeholder="Describe the role, responsibilities, and what makes this position exciting..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Description
                </label>
                <textarea
                  value={formData.companyDescription}
                  onChange={(e) => handleInputChange('companyDescription', e.target.value)}
                  rows={4}
                  placeholder="Tell candidates about your company, culture, and mission..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Requirements & Skills */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Requirements & Skills</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements
                </label>
                {formData.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={requirement}
                      onChange={(e) => handleArrayInputChange('requirements', index, e.target.value)}
                      placeholder="e.g., Bachelor's degree in Computer Science"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('requirements', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('requirements')}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Requirement
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Skills
                </label>
                {formData.skillsRequired.map((skill, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) => handleArrayInputChange('skillsRequired', index, e.target.value)}
                      placeholder="e.g., JavaScript, React, Node.js"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('skillsRequired', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('skillsRequired')}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Skill
                </button>
              </div>
            </div>
          </div>

          {/* Auto-Offer Section */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                <h2 className="text-xl font-semibold text-gray-900">Auto-Offer Settings</h2>
                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                  NEW
                </span>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="autoOfferEnabled"
                  checked={formData.autoOfferEnabled}
                  onChange={(e) => handleInputChange('autoOfferEnabled', e.target.checked)}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <label htmlFor="autoOfferEnabled" className="text-sm font-medium text-gray-900 cursor-pointer">
                    Enable Auto-Offer for Top Candidates
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Automatically approve the top qualifying candidate after a specified time period.
                    Candidates must meet all course and grade requirements to be eligible.
                  </p>
                </div>
              </div>

              {formData.autoOfferEnabled && (
                <div className="ml-7 space-y-6 pl-4 border-l-2 border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Auto-Offer Time Limit (hours)
                      </label>
                      <div className="relative">
                        <ClockIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input
                          type="number"
                          value={formData.autoOfferTimeLimit}
                          onChange={(e) => handleInputChange('autoOfferTimeLimit', parseInt(e.target.value))}
                          min="1"
                          max="168"
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-offer will trigger {formData.autoOfferTimeLimit} hours after job posting
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Overall Score (%)
                      </label>
                      <input
                        type="number"
                        value={formData.autoOfferMinimumScore || 80}
                        onChange={(e) => handleInputChange('autoOfferMinimumScore', parseInt(e.target.value))}
                        min="1"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum match score required for auto-offer eligibility
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Required Courses & Minimum Grades
                    </label>
                    {formData.autoOfferRequiredCourses.map((course, index) => (
                      <div key={index} className="flex items-center gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
                        <AcademicCapIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <select
                              value={course.courseId}
                              onChange={(e) => {
                                const selectedCourse = availableCourses.find(c => c.id === e.target.value);
                                handleCourseRequirementChange(index, 'courseId', e.target.value);
                                if (selectedCourse) {
                                  handleCourseRequirementChange(index, 'courseName', selectedCourse.title);
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-sm"
                              disabled={loadingCourses}
                            >
                              <option value="">Select a course...</option>
                              {availableCourses.map(availableCourse => (
                                <option key={availableCourse.id} value={availableCourse.id}>
                                  {availableCourse.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 whitespace-nowrap">Min Grade:</span>
                            <input
                              type="number"
                              value={course.minimumGrade}
                              onChange={(e) => handleCourseRequirementChange(index, 'minimumGrade', parseInt(e.target.value))}
                              min="0"
                              max="100"
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-green-500 focus:border-green-500"
                            />
                            <span className="text-xs text-gray-600">%</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCourseRequirement(index)}
                          className="p-1 text-red-600 hover:text-red-800 flex-shrink-0"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={addCourseRequirement}
                      disabled={loadingCourses}
                      className="flex items-center gap-2 text-green-600 hover:text-green-800 text-sm disabled:opacity-50"
                    >
                      <PlusIcon className="h-4 w-4" />
                      {loadingCourses ? 'Loading courses...' : 'Add Course Requirement'}
                    </button>
                    
                    {formData.autoOfferRequiredCourses.length === 0 && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md mt-2">
                        ⚠️ Without course requirements, auto-offer will only consider overall match score.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Compensation */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Compensation & Benefits</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <div className="relative">
                    <CurrencyDollarIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.salaryRange.min}
                      onChange={(e) => handleInputChange('salaryRange', {
                        ...formData.salaryRange,
                        min: parseInt(e.target.value)
                      })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <div className="relative">
                    <CurrencyDollarIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      value={formData.salaryRange.max}
                      onChange={(e) => handleInputChange('salaryRange', {
                        ...formData.salaryRange,
                        max: parseInt(e.target.value)
                      })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    value={formData.salaryRange.currency}
                    onChange={(e) => handleInputChange('salaryRange', {
                      ...formData.salaryRange,
                      currency: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Benefits
                </label>
                {formData.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={benefit}
                      onChange={(e) => handleArrayInputChange('benefits', index, e.target.value)}
                      placeholder="e.g., Health insurance, 401k, Flexible PTO"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('benefits', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('benefits')}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Benefit
                </button>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Posting...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="h-4 w-4" />
                  Post Job
                </>
              )}
            </button>
          </div>
        </form>

        {/* AI Modal */}
        {showAIModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-purple-600" />
                  AI Job Generator
                </h3>
                <button
                  onClick={() => setShowAIModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title or Description
                  </label>
                  <input
                    type="text"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="e.g., Senior React Developer"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <button
                  onClick={handleAIGeneration}
                  disabled={aiGenerating}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {aiGenerating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-4 w-4" />
                      Generate Job Description
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <SparklesIcon className="h-6 w-6 text-purple-600" />
                  AI Job Generator Premium
                </h3>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-purple-100 to-indigo-100 mb-4">
                  <SparklesIcon className="h-8 w-8 text-purple-600" />
                </div>
                
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Smart Job Posting with AI
                </h4>
                
                <p className="text-gray-600 mb-6">
                  Create compelling job descriptions that attract top talent using AI trained on high-quality job postings and current market data.
                </p>

                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 mb-6">
                  <h5 className="font-semibold text-gray-900 mb-2">🚀 Premium Features:</h5>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• AI-powered job descriptions that convert</li>
                    <li>• Market-optimized salary recommendations</li>
                    <li>• Industry-specific skill suggestions</li>
                    <li>• SEO-optimized content for better visibility</li>
                    <li>• Unlimited AI generations per month</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setShowUpgradeModal(false);
                      window.open('/subscription', '_blank');
                    }}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-medium flex items-center justify-center gap-2"
                  >
                    <SparklesIcon className="h-5 w-5" />
                    Upgrade to Premium
                  </button>
                  
                  <button
                    onClick={() => setShowUpgradeModal(false)}
                    className="w-full text-gray-600 hover:text-gray-800 py-2 text-sm"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostJobPage;